'use client';

import React, { useCallback, useState } from 'react';

import type * as stats from '@blockscout/stats-types';
import type { StatsIntervalIds } from 'types/client/stats';

import config from 'configs/app';

import ChartWidgetContainer from './ChartWidgetContainer';

import styles from './ChartsWidgetsList.module.css';

type Props = {
  filterQuery: string;
  initialFilterQuery: string;
  isError: boolean;
  isPlaceholderData: boolean;
  charts?: Array<stats.LineChartSection>;
  interval: StatsIntervalIds;
};

const ChartsWidgetsList = ({
  filterQuery,
  isError,
  isPlaceholderData,
  charts,
  interval,
  initialFilterQuery,
}: Props) => {
  const [ isSomeChartLoadingError, setIsSomeChartLoadingError ] = useState(false);
  const isAnyChartDisplayed = charts?.some(
    (section) => section.charts.length > 0,
  );
  const isEmptyChartList = Boolean(filterQuery) && !isAnyChartDisplayed;
  const sectionRef = React.useRef<HTMLUListElement | null>(null);

  const shouldScrollToSection = Boolean(initialFilterQuery);

  React.useEffect(() => {
    if (shouldScrollToSection) {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ shouldScrollToSection ]);

  const handleChartLoadingError = useCallback(
    () => setIsSomeChartLoadingError(true),
    [ setIsSomeChartLoadingError ],
  );

  if (isPlaceholderData && !isAnyChartDisplayed) {
    return (
      <div className={ styles.container }>
        <ul className={ styles.list }>
          <li className={ styles.listItem }>
            <div className={ styles.skeletonHeading }/>
            <div className={ styles.chartsGrid }>
              { Array.from({ length: 6 }).map((_, i) => (
                <div key={ i } className={ styles.skeletonChart }/>
              )) }
            </div>
          </li>
        </ul>
      </div>
    );
  }

  if (isEmptyChartList) {
    return (
      <div className={ styles.emptySearch }>
        <p>Couldn&apos;t find a chart that matches your filter query.</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={ styles.errorAlert }>
        <p>An error occurred while loading charts. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className={ styles.container }>
      { isSomeChartLoadingError && (
        <div className={ styles.errorAlert }>
          <p>Some charts failed to load.</p>
        </div>
      ) }

      <ul className={ styles.list } ref={ sectionRef }>
        { charts?.map((section) => (
          <li key={ section.id } className={ styles.listItem }>
            <h2 className={ styles.heading } id={ section.id }>
              { section.title?.replace(/\bETH\b/g, config.chain.currency.symbol) }
            </h2>

            <div className={ styles.chartsGrid }>
              { section.charts.map((chart) => (
                <ChartWidgetContainer
                  key={ chart.id }
                  id={ chart.id }
                  title={ chart.title?.replace(/\bETH\b/g, config.chain.currency.symbol) }
                  description={ chart.description?.replace(/\bETH\b/g, config.chain.currency.symbol) }
                  interval={ interval }
                  units={ chart.units === 'ETH' ? config.chain.currency.symbol : (chart.units || undefined) }
                  isPlaceholderData={ isPlaceholderData }
                  onLoadingError={ handleChartLoadingError }
                  href={ `/stats/${chart.id}` }
                />
              )) }
            </div>
          </li>
        )) }
      </ul>
    </div>
  );
};

export default ChartsWidgetsList;
